import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import gql from 'graphql-tag';
import useCurrentPage from '../../../hooks/useCurrentPage';
import FacilityLink from '../../stateless/FacilityLink';
import Price from '../../stateless/Price';
import Translation from '../../stateless/Translation';
import styles from './styles.scss';
import ApplicableFees from '../ApplicableFees';
import useCurrentSite from '../../../../modules/react/hooks/useCurrentSite';
import useSiteApplicableFees from '../../../../modules/react/hooks/useSiteApplicableFees';

const propTypes = {
  facility: PropTypes.shape({
    displayName: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    currencyLocale: PropTypes.string,
    minimumUnitPrice: PropTypes.number,
    address: PropTypes.shape({
      address1: PropTypes.string.isRequired,
      address2: PropTypes.string.isRequired,
      city: PropTypes.string.isRequired,
      state: PropTypes.string.isRequired,
      postal: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

const defaultProps = {};

const FacilityPopupContent = ({ facility }) => {
  const { displayName,
    address,
    currency,
    currencyLocale,
    minimumUnitPrice,
    applicableFeesEnabled } = facility || {};
  const { primaryColorClass, secondaryColorClass } = useCurrentPage();
  const { showApplicableFees } = useCurrentSite();

  const [showFeeDetails, setShowFeeDetails] = useState(false);
  const [shouldDisplayApplicableFee, setShouldDisplayApplicableFee] = useState(false);

  const [enabledStates, setEnabledStates] = useState([]);
  const { stateSettings } = useSiteApplicableFees();

  useEffect(() => {
    if (stateSettings?.applicableFeeSettings?.states) {
      const enabled = stateSettings.applicableFeeSettings.states
        .filter(state => state.enabled)
        .map(state => state.state);
      setEnabledStates(enabled);
    } else {
      setEnabledStates([]);
    }
  }, [stateSettings]);

  useEffect(() => {
    const isCAState = enabledStates.includes(address?.state);
    const shouldDisplay = isCAState
      && (showApplicableFees ?? true)
      && (applicableFeesEnabled ?? true);

    setShouldDisplayApplicableFee(shouldDisplay);
    setShowFeeDetails(false);
  }, [facility, address?.state, showApplicableFees, applicableFeesEnabled, enabledStates]);

  const linkClasses = cx(styles.name, primaryColorClass);

  const handleToggleFees = () => {
    setShowFeeDetails(prev => !prev);
  };

  const feeFilter = {
    facilityId: facility?.id,
  };

  const additionalFees = (
<ApplicableFees
  facility={facility}
  feeFilter={feeFilter}
  isMap
  isHead
  handleToggle={handleToggleFees}
  showDetails={showFeeDetails}
/>
  );

  return (
  <div className={`${styles.popupContainer} font-family-primary`}>
      {showFeeDetails ? (
        <div className={styles.feesDescriptionContainer}>
          {additionalFees}
          <ApplicableFees
            facility={facility}
            feeFilter={feeFilter}
            isMap
          />
        </div>
      ) : (
        <>
          <FacilityLink className={linkClasses} facility={facility} tabIndex="-1">
            {displayName}
          </FacilityLink>
          <div className={styles.address}>
            {address.address1} {address.address2}
          </div>
          <div className={styles.address}>
            {address.city},&nbsp;
            {address.state}&nbsp;
            {address.postal}
          </div>
          <div
            className={styles.pricing}
            data-testid="facilities-map-popup-pricing"
          >
            {minimumUnitPrice ? (
              <React.Fragment>
                <Translation
                  id="facilitiesMapPopup.minimumUnitPrice"
                  defaultMessage="Prices start at"
                />
                &nbsp;
                <span className={secondaryColorClass}>
                  <Price
                    currency={currency}
                    currencyLocale={currencyLocale}
                    value={minimumUnitPrice}
                  />
                </span>
                <div className={styles.applicableFeesLinkContainer}>
                  {shouldDisplayApplicableFee && additionalFees}
                </div>
              </React.Fragment>
            ) : (
              <Translation
                id="facilitiesMapPopup.callForPrice"
                defaultMessage="Call for prices"
              />
            )}
          </div>
        </>
      )}
  </div>
  );
};

FacilityPopupContent.propTypes = propTypes;
FacilityPopupContent.defaultProps = defaultProps;
FacilityPopupContent.fragments = {
  facility: gql`
    fragment FacilitiesMapPopupFacility on Facility {
      id
      address {
        address1
        address2
        city
        state
        postal
      }
      currency
      currencyLocale
      minimumUnitPrice
      displayName
      ...FacilityLinkFacility
    }

    ${FacilityLink.fragments.facility}
  `,
};

export const MarkerFragment = gql`
  fragment FacilitiesMapMarkerFacility on Facility {
    id
    ...FacilitiesMapPopupFacility
  }

  ${FacilityPopupContent.fragments.facility}
`;

export default FacilityPopupContent;
